/**
 * Blended eCPM presentation contract.
 *
 * MIRRORED FILE. Cortex and Quantum carry byte-identical copies, and a
 * backend parity test asserts they cannot drift.
 *
 * WHY IT EXISTS. Optimus showed a blended eCPM of $52.37 under the badge
 * "NO APPROVED TARGET", on the same page that said YouTube's CPM was above an
 * approved efficiency target. Both statements came from the approved-forecast
 * array, which is empty for every campaign on the platform, so every metric
 * fell through to a terminal "no target" state — including metrics whose
 * target is deterministically derivable from the approved plan.
 *
 * The portals do not derive anything here. The backend owns the arithmetic,
 * the authority tier and the verdict; this module decides only how they are
 * worded, so a derived planned blend is never presented as an approved target
 * and an absent benchmark is never presented as a failure.
 */

export const BLENDED_ECPM_CONTRACT = "optimus_planned_blended_benchmark_v1";

export const BLENDED_ECPM_STATE = Object.freeze({
  ABOVE: "ABOVE_PLANNED_BLENDED_BENCHMARK",
  ON: "ON_PLANNED_BLENDED_BENCHMARK",
  BELOW: "BELOW_PLANNED_BLENDED_BENCHMARK",
  NOT_ESTABLISHED: "BENCHMARK_NOT_ESTABLISHED",
});

/*
 * A higher eCPM than planned is a worse commercial outcome, so ABOVE is the
 * negative tone and BELOW the positive one. This is the opposite of a
 * delivery metric, and getting it backwards would colour an efficient
 * campaign red.
 */
const TONE_BY_STATE = Object.freeze({
  [BLENDED_ECPM_STATE.ABOVE]: "bad",
  [BLENDED_ECPM_STATE.ON]: "good",
  [BLENDED_ECPM_STATE.BELOW]: "good",
  [BLENDED_ECPM_STATE.NOT_ESTABLISHED]: "neutral",
});

export function resolveBlendedEcpmBadge(benchmark) {
  if (!benchmark || !benchmark.state) {
    return {
      label: "Benchmark not established",
      tone: "neutral",
      state: BLENDED_ECPM_STATE.NOT_ESTABLISHED,
    };
  }
  return {
    /*
     * The server's own wording. It distinguishes an explicitly approved
     * aggregate target from a derived planned blend, and the portal must not
     * flatten that distinction back into one phrase.
     */
    label: benchmark.stateLabel || "Benchmark not established",
    tone: TONE_BY_STATE[benchmark.state] || "neutral",
    state: benchmark.state,
  };
}

/**
 * The line under the value: what the benchmark is and how far delivery sits
 * from it. Null when no benchmark was established — the badge already says so
 * and repeating it adds nothing.
 */
export function resolveBlendedEcpmSubline(benchmark, { formatMoney } = {}) {
  if (!benchmark || benchmark.benchmarkCpm == null) return null;
  const money = typeof formatMoney === "function" ? formatMoney : (value) => `$${Number(value).toFixed(2)}`;
  const kind =
    benchmark.authority === "APPROVED_AGGREGATE_BENCHMARK"
      ? "Approved aggregate target"
      : "Planned blended benchmark";
  const variance =
    typeof benchmark.variancePercent === "number"
      ? ` · ${benchmark.variancePercent > 0 ? "+" : ""}${benchmark.variancePercent}% vs plan`
      : "";
  return `${kind} ${money(benchmark.benchmarkCpm)}${variance}`;
}

/**
 * Why the benchmark could not be established, in plain words.
 *
 * The blockers are governed enum codes; showing them raw would be honest but
 * unreadable, and showing nothing would leave "Benchmark not established"
 * looking like a defect rather than a stated condition.
 */
export function resolveBlendedEcpmUnavailableReason(benchmark) {
  const blockers = benchmark?.derivation?.blockers;
  if (!Array.isArray(blockers) || !blockers.length) return null;

  const reasons = new Set();
  blockers.forEach((code) => {
    if (code === "NO_APPROVED_ALLOCATION") reasons.add("no approved channel allocation");
    else if (code === "ALLOCATION_NOT_APPROVED") reasons.add("the channel allocation is not approved");
    else if (code.startsWith("NO_PLAN_CPM_")) {
      reasons.add(`the approved plan states no CPM assumption for ${code.replace("NO_PLAN_CPM_", "").toLowerCase()}`);
    } else if (code.startsWith("CONFLICTING_PLAN_CPM_")) {
      reasons.add(
        `the approved plan states more than one CPM assumption for ${code
          .replace("CONFLICTING_PLAN_CPM_", "")
          .toLowerCase()}`
      );
    } else if (code.startsWith("DELIVERY_OUTSIDE_APPROVED_ALLOCATION_")) {
      reasons.add(
        `${code.replace("DELIVERY_OUTSIDE_APPROVED_ALLOCATION_", "").toLowerCase()} delivered but is not in the approved allocation`
      );
    }
  });
  if (!reasons.size) return null;
  return `No blended benchmark: ${[...reasons].join("; ")}.`;
}

/**
 * The channel decomposition, and the sentence that stops a blended number
 * from condemning the whole media architecture.
 *
 * A blended eCPM can hide opposing channel results. Where one channel is
 * above its planned rate and another is on or below it, that must be said
 * explicitly and by name — the aggregate alone would read as a uniform
 * failure when in fact one channel is beating plan.
 */
export function resolveBlendedEcpmDecomposition(benchmark) {
  if (!benchmark || !Array.isArray(benchmark.decomposition) || !benchmark.decomposition.length) {
    return null;
  }
  const drivers = benchmark.varianceDrivers || null;
  const rows = benchmark.decomposition.map((row) => ({
    channel: row.channel,
    label: row.label || row.channel,
    actualClientBilledSpend: row.actualClientBilledSpend,
    actualImpressions: row.actualImpressions,
    actualClientBilledCpm: row.actualClientBilledCpm,
    plannedClientBilledCpm: row.plannedClientBilledCpm,
    variance: row.variance,
    variancePercent: row.variancePercent,
    impressionShare: row.impressionShare,
    plannedImpressionShare: row.plannedImpressionShare,
    contributionToBlendedCpm: row.contributionToBlendedCpm,
    rateEffectOnBlendedCpm: row.rateEffectOnBlendedCpm,
    mixEffectOnBlendedCpm: row.mixEffectOnBlendedCpm,
    state: row.state,
    tone: row.state === "ABOVE_PLAN" ? "bad" : row.state === "BELOW_PLAN" ? "good" : "neutral",
  }));

  const above = rows.filter((row) => row.state === "ABOVE_PLAN");
  const onOrBelow = rows.filter((row) => row.state === "ON_PLAN" || row.state === "BELOW_PLAN");

  let headline = null;
  if (above.length && onOrBelow.length) {
    const namesAbove = above.map((row) => row.label).join(" and ");
    const namesBelow = onOrBelow.map((row) => row.label).join(" and ");
    headline =
      `${namesBelow} ${onOrBelow.length === 1 ? "is" : "are"} at or better than the planned rate; ` +
      `${namesAbove} ${above.length === 1 ? "is" : "are"} above plan and ${
        above.length === 1 ? "is" : "are"
      } driving the blended variance.`;
  } else if (above.length && !onOrBelow.length) {
    headline = `${above.map((row) => row.label).join(" and ")} ${
      above.length === 1 ? "is" : "are"
    } above the planned rate.`;
  } else if (onOrBelow.length && !above.length) {
    headline = `${onOrBelow.map((row) => row.label).join(" and ")} ${
      onOrBelow.length === 1 ? "is" : "are"
    } at or better than the planned rate.`;
  }

  /*
   * Rate versus mix. A blend can sit above plan with every channel on its
   * planned rate, purely because delivery skewed toward the pricier channel.
   * Naming which effect moved the number is the difference between a real
   * finding and a misattributed one.
   */
  let effectNote = null;
  if (drivers && typeof drivers.rateEffectOnBlendedCpm === "number") {
    const rate = drivers.rateEffectOnBlendedCpm;
    const mix = drivers.mixEffectOnBlendedCpm;
    const describe = (value) => `${value >= 0 ? "+" : "-"}$${Math.abs(value).toFixed(2)}`;
    effectNote =
      drivers.dominantEffect === "MIX"
        ? `Driven mainly by channel mix (${describe(mix)}) rather than rate (${describe(rate)}).`
        : drivers.dominantEffect === "RATE"
        ? `Driven mainly by rate (${describe(rate)}) rather than channel mix (${describe(mix)}).`
        : `Rate ${describe(rate)} and channel mix ${describe(mix)} contribute comparably.`;
  }

  return {
    rows,
    headline,
    effectNote,
    blendMasksOpposingChannelResults: Boolean(drivers?.blendMasksOpposingChannelResults),
    evidenceClass: drivers?.evidenceClass || null,
  };
}
